import { flow, forEach, get, head } from 'lodash';
import { change, formValueSelector, getFormSyncErrors, InjectedFormProps, reduxForm, submit } from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { actions as quoteActions, actions } from '../../../../services/quote/reducer';
import { push } from 'react-router-redux';
import { IAuthState } from '../../../../services/auth/models';
import {
  IBilling,
  IChannel,
  IDocument,
  IOptionMap,
  IPremium,
  IProduct,
  IQuotes,
  ISignposted,
  IStateCities,
  ITraveller,
  TripType
} from '../../../../data/api/models';
import { IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import { II18nMessages } from '../../../../services/branding/models';
import { renewalTypeSelectionEnum } from '../../components/YourRenewal';
import * as React from 'react';
import { Form } from 'react-bootstrap';
import { CalculationError } from '../Components/CalculationError';
import { PriceDisplay } from '../Components/PriceDisplay';
import { SectionName } from '../QuotePayment/Components/SectionName';
import { QuoteSummary } from '../QuotePayment/Components/QuoteSummary';
import { MedicalDetails } from '../QuotePayment/Components/MedicalLoad';
import { DocumentPreferences } from '../QuotePayment/Components/DocumentPreferences';
import { PaymentTermsAndConditions } from '../QuotePayment/Components/PaymentTermsAndConditions';
import { PaymentConsent } from '../QuotePayment/Components/PaymentConsent';
import { FooterPriceDisplay } from '../Components/FooterPriceDisplay';
import { TermsAndConditions } from '../Components/TermsAndConditions';
import { QuoteStepButtons } from '../Components/QuoteStepButtons';
import { AggregatorDisplay } from './Components/AggregatorDisplay';
import { shouldIgnoreCardPermissions } from '../../../../helpers/Quote';
import EmergencyMessaging, { EmergencyMessagingEnum } from '../../../../components/EmergencyMessaging';
import { PaymentErrorMessage } from '../QuotePayment/Components/PaymentErrorMessage';

interface IQuotePaymentProps extends InjectedFormProps {
  auth: IAuthState;
  premium: IPremium;
  travellers: ITraveller[];
  tripType: TripType;
  quote: IQuoteState;
  options: IOptionMap;
  quotes: IQuotes;
  groupType: string;
  region: string;
  schemeId: string,
  stateCities: IStateCities[];
  product: IProduct;
  communications: any;
  i18n: II18nMessages;
  synchronousError: any;
  documents: IDocument[];
  signposted: ISignposted;
  renewalStatus: renewalTypeSelectionEnum;
  documentPack: string;
  permissions: boolean;
  channel: IChannel;
  billing: IBilling;
  errorMessage: any;
  quoteReference: string;
  quoteState: IQuoteState;
  destinationsValue: string[];
  stateCitiesValue: string[];
  startDate: string;
  endDate: string;
  campaignCode: any;
  medicalsDeclared?: boolean;
  maxTripDuration: any;
  actions: {
    quote: IQuoteActions;
    submit(name: string);
  }

  change(field: string, value: any): void;

  touch(form: string, field: string): void

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

class AggPayment extends React.Component<IQuotePaymentProps, any> {

  constructor(props) {
    super(props);
    const { change, touch } = props;
    change('permissions', true);
    change('medicalsDeclared', null);
    this.recalculate = this.recalculate.bind(this);
    change('preventPatch', true);
    this.recalculate(renewalTypeSelectionEnum.AUTO);
    this.state = {
      travellersEditMode: false,
      showMissingDetails: false,
    }
    touch('quote', 'travellers.[0].phone');
    touch('quote', 'travellers.[0].address.postcode');
    touch('quote', 'medicalsDeclared');
  }

  private getSelectedPremium = (): any => {
    const { actions, schemeId, tripType } = this.props;
    return actions.quote.getQuoteSelectedPremium(schemeId, tripType);
  }

  protected checkForErrors = () => {
    const { actions, valid } = this.props;
    const { travellersEditMode } = this.state;

    this.setState({ showMissingDetails: false })

    if (travellersEditMode) {
      this.setState({ showMissingDetails: true })
      window.scrollTo(0, 0);
    } else {
      Promise.resolve(actions.submit('quote')).then(() => {
        if (!valid) {
          const errorBlock = head(document.getElementsByClassName('error-block-container'))
          if (errorBlock) {
            errorBlock.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      });
    }
  }

  private handleTravellersEditMode = (value) => {
    this.setState({ travellersEditMode: value })
    if (!value) {
      this.setState({ showMissingDetails: false })
    }
  }

  private readonly recalculate = (renewalStatus: string = '') => {
    const { actions, change } = this.props;
    Promise.resolve(actions.quote.calculate(null, true, true)).then((data) => {
      if (renewalStatus) {
        change('renewalStatus', renewalStatus);
      }
    });
  }

  render() {
    const {
      handleSubmit,
      travellers,
      submitting,
      submitFailed,
      valid,
      schemeId,
      tripType,
      documents,
      signposted,
      product,
      permissions,
      quoteState,
      change,
      channel,
      billing,
      quote,
      region,
      destinationsValue,
      premium,
      options,
      startDate,
      endDate,
      documentPack,
      groupType,
      synchronousError,
      medicalsDeclared,
      maxTripDuration,
      renderDefinition,
      errorMessage,
      quoteReference
    } = this.props;

    const { showMissingDetails } = this.state;

    const leadTraveller = head(travellers);
    const selectedPremium = this.getSelectedPremium();
    const shouldShowRenewalSection = tripType === 'annual';
    const ignorePermissions = shouldIgnoreCardPermissions();

    if (!permissions && !ignorePermissions) {
      change('renewalStatus', renewalTypeSelectionEnum.MANUAL)
    }

    if (get(quoteState, 'calculationError.type')) {
      return (
        <div className="steps-container error">
          <CalculationError calculationError={quoteState.calculationError}/>
        </div>
      )
    }

    return (
      <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">
        <div className="steps-container">

          <PriceDisplay
            leadTraveller={leadTraveller}
            title={'Here\'s your quote for'}
            tripType={tripType}
            premium={selectedPremium}
            infoText={'Please check your cover details before payment'}
            {...this.props}
          />

          <EmergencyMessaging type={EmergencyMessagingEnum.AGGREGATOR}/>

          <PaymentErrorMessage
            errorMessage={errorMessage}
            submitFailed={submitFailed}
            valid={valid}
            quoteReference={quoteReference}
            showMissingDetails={showMissingDetails}
          />

          <div style={{ marginTop: '20px' }}/>

          <SectionName imageName={'quote-summary-black.svg'} title={'Quote Summary'}/>

          <AggregatorDisplay channel={channel}/>

          <div style={{ marginTop: '20px' }}/>

          <QuoteSummary
            travellers={travellers}
            travellersInEditmode={this.handleTravellersEditMode}
            tripType={tripType}
            region={region}
            destinationsValue={destinationsValue}
            premium={premium}
            schemeId={schemeId}
            options={options}
            product={product}
            startDate={startDate}
            endDate={endDate}
            groupType={groupType}
            recalculate={this.recalculate}
            synchronousError={synchronousError}
            maxTripDuration={maxTripDuration}
            renderDefinition={renderDefinition}
            channel={channel}
            {...this.props}
          />

          <SectionName imageName={'important-statements.svg'} title={'Medical details'}/>

          <MedicalDetails
            travellers={travellers}
            product={product}
            isCalculating={quoteState.isCalculating}
            isSignposted={get(signposted, 'medicalLoad')}
            premium={premium}
            isAggregator={true}
            {...this.props}
            channel={channel}/>

          <div style={{ marginTop: '20px' }}/>

          <div style={{ color: '#0045A0', backgroundColor: '#e3f1fe', fontWeight: 'bold', padding: '10px' }}>
            Please make sure you don’t already have cover in place.
          </div>

          <div style={{ marginTop: '20px' }}/>

          <PaymentTermsAndConditions/>

          <div style={{ marginTop: '20px' }}/>

          <DocumentPreferences {...this.props} documentPack={documentPack}/>

          <div style={{ marginTop: '20px' }}/>

          <PaymentConsent
            travellers={travellers}
            channel={channel}
            shouldShowRenewalSection={shouldShowRenewalSection}
            ignorePermissions={ignorePermissions}
            permissions={permissions}
            billing={billing}
            {...this.props}/>

          <div style={{ marginTop: '20px' }}/>

          <FooterPriceDisplay premium={selectedPremium} tripType={tripType}/>

          <TermsAndConditions documents={documents} channel={channel}/>

          <div style={{ marginTop: '50px', display: medicalsDeclared === false ? 'none' : '' }}>
            <QuoteStepButtons
              submitting={submitting}
              hasBackButton={false}
              submitTitle={'CONFIRM AND PAY'}
              submitFailed={submitFailed}
              submitButtonType={'payment'}
              onClick={this.checkForErrors}
              valid={valid}
              disable={quote.waitForPurchaseToComplete}
            />
          </div>

        </div>
      </Form>
    );
  }
}

const validate = (values) => {
  const errors = {
    travellers: [
      {
        address: {
          postcode: null,
        }
      }
    ],
  };

  if (!get(values, 'travellers[0].address.line1', null)) {
    errors.travellers[0].address.postcode = 'Please use the Search button to select an address.';
  }
  return errors;
};

let selector;
export default flow([
  reduxForm({
    form: 'quote',
    validate,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const synchronousError = getFormSyncErrors('quote')(state);
      const errorMessage = state.quote.errorMessage;
      const quotes = state.quote.quotes;
      const quoteState = state.quote;
      const travellers = selector(state, 'travellers');
      const tripType = selector(state, 'tripType');
      const groupType = selector(state, 'groupType');
      const documents = selector(state, 'documents');
      const signposted = selector(state, 'signposted');
      const schemeId = selector(state, 'schemeId');
      const communications = selector(state, 'communications');
      const renewalStatus = selector(state, 'renewalStatus');
      const permissions = selector(state, 'permissions');
      const billing = selector(state, 'billing');
      const quoteReference = selector(state, 'quoteReference');
      const region = selector(state, 'region');
      const options = selector(state, 'options');
      const startDate = selector(state, 'startDate');
      const endDate = selector(state, 'endDate');
      const destinationsValue = selector(state, 'destinations');
      const stateCitiesValue = selector(state, 'stateCities');
      const documentPack = selector(state, 'documentPack');
      const campaignCode = selector(state, 'campaignCode');
      const medicalsDeclared = selector(state, 'medicalsDeclared');
      const maxTripDuration = selector(state, 'maxTripDuration');

      let premium = null;

      forEach(quotes, (tripTypes, level) => {
        forEach(tripTypes, (quotedPremium) => {
          if (quotedPremium.scheme.id === schemeId) {
            premium = quotedPremium;
          }
        });
      });
      return {
        auth: state.auth,
        i18n: state.branding.i18n,
        quoteState,
        synchronousError,
        travellers,
        tripType,
        groupType,
        documents,
        signposted,
        schemeId,
        communications,
        renewalStatus,
        permissions,
        billing,
        errorMessage,
        quoteReference,
        region,
        destinationsValue,
        premium,
        options,
        startDate,
        endDate,
        documentPack,
        stateCitiesValue,
        campaignCode,
        medicalsDeclared,
        maxTripDuration
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        submit: bindActionCreators(submit, dispatch),
        calculate: bindActionCreators(actions.calculate, dispatch),
        push: bindActionCreators(push, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        change: bindActionCreators(change, dispatch),
      },
    }),
  ),
])(AggPayment);
